import { computed, Injectable, Signal, signal } from '@angular/core';
import { FlowNodeType } from '../enums/flow-node-type.enum';
import { FlowZoomContext } from '../models/flow-zoom-context.model';

/**
 * Single source of truth for the flow focus (zoom) mode.
 *
 * The service owns **only** the zoom state. Building the zoomed graph and routing the zoom events
 * stay with the consumer, which knows its own domain: the service is deliberately unaware of what
 * a node contains.
 */
@Injectable({ providedIn: 'root' })
export class FlowZoomService {
  /** Current focus context, `null` when the full flow is displayed. */
  readonly context: Signal<FlowZoomContext | null>;
  /** True while the flow is zoomed on a single node. */
  readonly isZoomed: Signal<boolean>;
  /** Id of the node the flow is zoomed on, `null` when the full flow is displayed. */
  readonly zoomedNodeId: Signal<string | null>;
  /** Id of the entity focused inside the zoomed node, `null` when none is selected. */
  readonly zoomedTargetId: Signal<string | null>;

  private readonly contextSignal = signal<FlowZoomContext | null>(null);

  constructor() {
    this.context = this.contextSignal.asReadonly();
    this.isZoomed = computed(() => this.contextSignal() !== null);
    this.zoomedNodeId = computed(() => this.contextSignal()?.nodeId ?? null);
    this.zoomedTargetId = computed(() => this.contextSignal()?.targetId ?? null);
  }

  /** Enters the focus mode on a node. Any previously focused target is dropped. */
  zoomInto(nodeType: FlowNodeType, nodeId: string): void {
    this.contextSignal.set({ nodeType, nodeId });
  }

  /** Narrows the zoomed flow to an entity of the zoomed node. No-op while the flow is not zoomed. */
  selectTarget(targetId: string): void {
    const context = this.contextSignal();
    if (!context) {
      return;
    }
    this.contextSignal.set({ ...context, targetId });
  }

  /** Leaves the focus mode: the full flow is restored. */
  exit(): void {
    this.contextSignal.set(null);
  }
}
